import { compose, setDisplayName } from 'react-recompose'
import { withRouter } from 'react-router'
import { getPath } from '@anewgo/functions'

import { graphqlProps, withFields } from '../../hocs'
import {
  ELEVATION_STORIES_ONLY,
  LOT_INVENTORY_EXTERIOR_ONLY,
  LOT_SITEPLAN_INFO,
  SCHEME,
} from '../../graphql'

const ExpandedSelection = compose(
  withRouter,

  withFields({
    query: ELEVATION_STORIES_ONLY,
    skip: ({ loadElevationStories, selection }) =>
      !getPath('elevation')(selection) || !loadElevationStories,
    requiredProps: {
      selection: `
        stories {
          id
          communityId
          planId
          elevationId
          name
          fnum
          optional
        }
      `,
    },
    options: ({ match, selection }) => ({
      variables: {
        clientName: match.params.clientName,
        communityId: getPath('community', 'id')(selection),
        planId: getPath('plan', 'id')(selection),
        elevationId: getPath('elevation', 'id')(selection),
      },
    }),
    props: graphqlProps('elevation', {
      mapResultsToProps: (elevationResult, ownProps) => ({
        selection: {
          ...ownProps.selection,
          elevation: {
            ...ownProps.selection.elevation,
            stories: elevationResult.stories,
          },
        },
      }),
    }),
  }),

  withFields({
    query: LOT_INVENTORY_EXTERIOR_ONLY,
    skip: ({ loadInventoryExteriorConfiguration, selection }) =>
      !getPath('lot', 'inventory')(selection) ||
      !loadInventoryExteriorConfiguration,
    requiredProps: {
      selection: `
        lot {
          inventory {
            exteriorConfiguration {
              inventoryId
              scheme {
                id
                name
                materials {
                  id
                  materialId
                  materialCategory
                  material
                  name
                  hex
                  swatch
                  masonryLib
                }
              }
              paletteElementSelections {
                inventoryId
                elementId
                paletteId
                paletteSelection {
                  id
                }
              }
            }
          }
        }
      `,
    },
    options: ({ match, selection }) => ({
      variables: {
        clientName: match.params.clientName,
        lotId: selection.lot.id,
      },
    }),
    props: graphqlProps('lot', {
      mapResultsToProps: (lotResult, ownProps) => ({
        selection: {
          ...ownProps.selection,
          lot: {
            ...ownProps.selection.lot,
            inventory: {
              ...ownProps.selection.lot.inventory,
              exteriorConfiguration: lotResult.inventory.exteriorConfiguration,
            },
          },
        },
      }),
    }),
  }),

  withFields({
    query: LOT_SITEPLAN_INFO,
    skip: ({ loadLotSiteplanInfo, selection }) =>
      !getPath('lot')(selection) || !loadLotSiteplanInfo,
    requiredProps: {
      selection: `
        lot {
          siteplanName
          siteplanInfo {
            lotId
            siteplanId
            x
            y
          }
        }
      `,
    },
    options: ({ match, selection }) => ({
      variables: {
        clientName: match.params.clientName,
        lotId: selection.lot.id,
      },
    }),
    props: graphqlProps('lot', {
      mapResultsToProps: (lotResult, ownProps) => ({
        selection: {
          ...ownProps.selection,
          lot: {
            ...ownProps.selection.lot,
            siteplanName: lotResult.siteplanName,
            siteplanInfo: lotResult.siteplanInfo,
          },
        },
      }),
    }),
  }),

  withFields({
    query: SCHEME,
    skip: ({ loadSchemeMaterials, selection }) =>
      !getPath('scheme')(selection) || !loadSchemeMaterials,
    requiredProps: {
      selection: `
        scheme {
          id
          name
          materials {
            id
            materialId
            materialCategory
            material
            name
            hex
            swatch
            masonryLib
          }
        }
      `,
    },
    options: ({ match, selection }) => ({
      variables: {
        clientName: match.params.clientName,
        schemeId: selection.scheme.id,
      },
    }),
    props: graphqlProps('scheme', {
      mapResultsToProps: ([schemeResult], ownProps) => ({
        selection: {
          ...ownProps.selection,
          scheme: {
            ...ownProps.selection.scheme,
            materials: schemeResult.materials,
          },
        },
      }),
    }),
  }),

  setDisplayName('ExpandedSelection')
)((props) => {
  const { children, selection } = props
  return children(selection)
})

export default ExpandedSelection
